import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-scroll";
import { Menu, X, Download, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
// @ts-ignore
import cv from "../assets/cv.pdf";

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: t("header.about"), to: "about" },
    { name: t("header.work"), to: "work" },
    { name: t("header.projects"), to: "projects" },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageMenuOpen(false);
  };

  const currentLanguage = i18n.language === "es" ? "ES" : "EN";

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-slate-900/95 backdrop-blur-lg border-b border-white/10"
          : "bg-transparent"
      }`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <nav className="section-padding container-width">
        <div className="flex items-center justify-between py-4">
          <motion.div
            className="text-2xl font-bold gradient-text"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Link
              to="hero"
              smooth={true}
              duration={500}
              className="cursor-pointer"
            >
              JB
            </Link>
          </motion.div>

          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.div
                key={item.name}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={item.to}
                  smooth={true}
                  duration={500}
                  offset={-80}
                  className="text-gray-300 hover:text-white cursor-pointer transition-colors duration-200 font-medium"
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}

            <div className="relative">
              <motion.button
                className="glass-card p-3 hover:bg-white/20 transition-all duration-300 group"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <div className="flex items-center space-x-2">
                  <Globe
                    size={18}
                    className="text-white group-hover:text-blue-400 transition-colors"
                  />
                  <span className="text-white font-medium">
                    {currentLanguage}
                  </span>
                </div>
              </motion.button>

              <AnimatePresence>
                {isLanguageMenuOpen && (
                  <motion.div
                    className="absolute top-full right-0 mt-2 glass-card rounded-lg overflow-hidden shadow-xl"
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.button
                      onClick={() => changeLanguage("en")}
                      className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-white/20 transition-all duration-200 w-full text-left"
                      whileHover={{ scale: 1.02 }}
                    >
                      <span className="text-sm">🇺🇸</span>
                      <span>{t("language.english")}</span>
                    </motion.button>
                    <motion.button
                      onClick={() => changeLanguage("es")}
                      className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-white/20 transition-all duration-200 w-full text-left"
                      whileHover={{ scale: 1.02 }}
                    >
                      <span className="text-sm">🇪🇸</span>
                      <span>{t("language.spanish")}</span>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.a
              href={cv}
              download="James-Bell-CV.pdf"
              className="btn-primary flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Download size={18} />
              <span>{t("header.cv")}</span>
            </motion.a>
          </div>

          <motion.button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden glass-card mt-4 p-6 rounded-lg"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.to}
                    smooth={true}
                    duration={500}
                    offset={-80}
                    className="text-gray-300 hover:text-white cursor-pointer transition-colors duration-200 font-medium py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}

                <div className="flex flex-col space-y-4">
                  <div className="flex justify-center">
                    <div className="glass-card rounded-lg p-2">
                      <div className="flex space-x-2">
                        <motion.button
                          onClick={() => changeLanguage("en")}
                          className={`flex items-center space-x-2 px-3 py-2 rounded transition-all duration-200 ${
                            i18n.language === "en"
                              ? "bg-blue-500/30 text-blue-300"
                              : "text-white hover:bg-white/20"
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <span className="text-sm">🇺🇸</span>
                          <span className="text-sm">EN</span>
                        </motion.button>
                        <motion.button
                          onClick={() => changeLanguage("es")}
                          className={`flex items-center space-x-2 px-3 py-2 rounded transition-all duration-200 ${
                            i18n.language === "es"
                              ? "bg-blue-500/30 text-blue-300"
                              : "text-white hover:bg-white/20"
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <span className="text-sm">🇪🇸</span>
                          <span className="text-sm">ES</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  <a
                    href={cv}
                    download="James-Bell-CV.pdf"
                    className="btn-primary flex items-center justify-center space-x-2"
                  >
                    <Download size={18} />
                    <span>{t("header.downloadCv")}</span>
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;
